import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { AppPlatform, AppVersionsOut, StaffAppVersion } from '../api/types'
import { Kpi } from '../components/Kpi'
import { PageHeader } from '../components/Layout'
import { Panel } from '../components/States'
import { count, formatDateTime, humanise } from '../lib/format'

const PLATFORM: Record<AppPlatform, string> = { android: 'Android', ios: 'iPhone' }

function newestFor(data: AppVersionsOut, platform: AppPlatform | null): number | null {
  if (platform === 'android') return data.newest_android_build
  if (platform === 'ios') return data.newest_ios_build
  return null
}

/** On an older build than someone else on the same kind of phone. */
function behind(data: AppVersionsOut, person: StaffAppVersion): boolean {
  const newest = newestFor(data, person.app_platform)
  return person.app_build !== null && newest !== null && person.app_build < newest
}

function olderApp(person: StaffAppVersion): boolean {
  return person.app_seen_at !== null && person.app_build === null
}

/** Older apps first, then lowest build to highest, then people not on the app:
 *  whoever is furthest behind is at the top. */
function rank(person: StaffAppVersion): number {
  if (olderApp(person)) return -1
  if (person.app_build === null) return Number.MAX_SAFE_INTEGER
  return person.app_build
}

/**
 * Which build each person's phone is on. Nothing to manage here: Google Play
 * has every Android phone update the next time Fulfil opens after a new build
 * is released to the track.
 */
export function AppVersionsPage() {
  const query = useQuery({
    queryKey: ['app-versions'],
    queryFn: () => api.get<AppVersionsOut>('/app-versions'),
  })
  const data = query.data
  const people = [...(data?.staff ?? [])].sort(
    (a, b) => rank(a) - rank(b) || a.full_name.localeCompare(b.full_name),
  )
  const behindCount = data ? people.filter((person) => behind(data, person)).length : 0
  const older = people.filter(olderApp).length
  const onBuild = (platform: AppPlatform, build: number | null) =>
    people.filter((p) => p.app_platform === platform && p.app_build === build).length

  return (
    <>
      <PageHeader title="App versions" subtitle="Which build each person's phone is on">
        <button className="btn ghost sm" onClick={() => query.refetch()} disabled={query.isFetching}>
          {query.isFetching ? 'Loading…' : 'Refresh'}
        </button>
      </PageHeader>

      <div className="content stack">
        <Panel isLoading={query.isPending} error={query.error}>
          {data && (
            <>
              <div className="grid cols-4">
                <Kpi
                  label="Newest Android build"
                  value={data.newest_android_build ?? '—'}
                  foot={
                    data.newest_android_build
                      ? `${count(onBuild('android', data.newest_android_build))} on it`
                      : undefined
                  }
                />
                <Kpi
                  label="Newest iPhone build"
                  value={data.newest_ios_build ?? '—'}
                  foot={
                    data.newest_ios_build ? `${count(onBuild('ios', data.newest_ios_build))} on it` : undefined
                  }
                />
                <Kpi label="Behind" value={count(behindCount)} foot="On an older build" />
                <Kpi label="Older app" value={count(older)} foot="From before version tracking" />
              </div>

              <div className="alert info">
                Android phones update themselves: Google Play asks them to the next time Fulfil opens after a
                build is released to the internal testing track.
                {older > 0 &&
                  ' Apps from before version tracking can’t ask, so Play updates those in the background, or open the Play Store on the phone and tap Update.'}
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>People</h2>
                  <span className="hint">{count(people.length)} active</span>
                </div>
                <div className="card-body flush">
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Version</th>
                          <th>Phone</th>
                          <th className="num">Last used</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {people.map((person) => (
                          <tr key={person.id}>
                            <td className="strong">{person.full_name}</td>
                            <td className="cell-sub">{humanise(person.role)}</td>
                            <td>
                              {person.app_build !== null
                                ? `${person.app_version ?? '?'} (${person.app_build})`
                                : olderApp(person)
                                  ? 'Older app'
                                  : '—'}
                            </td>
                            <td className="cell-sub">
                              {person.app_platform ? PLATFORM[person.app_platform] : '—'}
                            </td>
                            <td className="num cell-sub">{formatDateTime(person.app_seen_at)}</td>
                            <td>
                              <StatusChip data={data} person={person} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </Panel>
      </div>
    </>
  )
}

function StatusChip({ data, person }: { data: AppVersionsOut; person: StaffAppVersion }) {
  if (behind(data, person)) return <span className="chip amber">Behind</span>
  if (olderApp(person)) return <span className="chip amber">Older app</span>
  if (person.app_seen_at === null) return <span className="chip grey">Not on the app</span>
  return <span className="chip green">Newest</span>
}
